// src/components/rubrica/RubricaManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  useDisclosure,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  Spacer,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Checkbox,
  useColorModeValue,
  Grid,
  GridItem,
  Tooltip,
  ButtonGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer
} from '@chakra-ui/react';
import { 
  AddIcon, 
  EditIcon, 
  DeleteIcon, 
  SearchIcon, 
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@chakra-ui/icons';
import { 
  FiUsers, 
  FiMail, 
  FiPhone, 
  FiBriefcase, 
  FiUpload,
  FiDownload,
  FiEdit3,
  FiTrash2
} from 'react-icons/fi';
import ContactForm from './ContactForm';
import './RubricaManager.css';
const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const RubricaManager = ({ bot, onUpdate }) => {
  const [rubrica] = useState(true); // Always true as requested
  const [contacts, setContacts] = useState(bot?.contacts || []);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterBy, setFilterBy] = useState('all');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onClose: onDeleteModalClose 
  } = useDisclosure();
  
  const toast = useToast();

  // Theme colors
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const textTertiary = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (bot?.id) {
      loadContacts();
    }
  }, [bot]);

  // Filter and search logic
  useEffect(() => {
    let filtered = [...contacts];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.phone?.includes(searchQuery)
      );
    }

    // Apply status filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(contact => {
        switch (filterBy) {
          case 'called':
            return contact.called;
          case 'not-called':
            return !contact.called;
          case 'company':
            return contact.company;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredContacts(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [contacts, searchQuery, filterBy, sortBy, sortOrder]);

  // Pagination logic
  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContacts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredContacts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  const loadContacts = async () => {
    // Use existing contacts data - no API call needed
    setInitialLoading(false);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedContacts(new Set(paginatedContacts.map(c => c.id)));
    } else {
      setSelectedContacts(new Set());
    }
  };

  const handleSelectContact = (contactId, checked) => {
    const newSelected = new Set(selectedContacts);
    if (checked) {
      newSelected.add(contactId);
    } else {
      newSelected.delete(contactId);
    }
    setSelectedContacts(newSelected);
  };

  const handleBulkDelete = async () => {
    setLoading(true);
    try {
      // Mock API call - replace with real implementation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setContacts(contacts.filter(c => !selectedContacts.has(c.id)));
      setSelectedContacts(new Set());
      onDeleteModalClose();
      
      toast({
        title: 'Success',
        description: `${selectedContacts.size} contact(s) deleted successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete contacts',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async (savedContact) => {
    if (editingContact) {
      setContacts(contacts.map(c => 
        c.id === savedContact.id ? savedContact : c
      ));
      toast({
        title: 'Success',
        description: 'Contact updated successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } else {
      const newContact = {
        ...savedContact,
        id: Date.now() // Mock ID generation
      };
      setContacts([...contacts, newContact]);
      toast({
        title: 'Success',
        description: 'Contact created successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
    
    onClose();
    setEditingContact(null);
    onUpdate && onUpdate();
  };

  const handleDeleteContact = async (contactId) => {
    setContacts(contacts.filter(c => c.id !== contactId));
    toast({
      title: 'Success',
      description: 'Contact deleted successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (initialLoading) {
    return (
      <Card bg={cardBg} shadow="xl" borderRadius="2xl">
        <CardBody>
          <VStack spacing={4} py={12}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color={textSecondary} fontSize="lg">Loading contacts...</Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  const isAllSelected = paginatedContacts.length > 0 && 
    paginatedContacts.every(contact => selectedContacts.has(contact.id));
  const isIndeterminate = selectedContacts.size > 0 && !isAllSelected;

  return (
    <Box>
      {/* Header with Controls */}
      <Card bg={cardBg} shadow="xl" borderRadius="2xl" borderWidth="1px" borderColor={borderColor} mb={6}>
        <CardHeader pb={0}>
          <Flex direction={{ base: 'column', lg: 'row' }} gap={4}>
            <VStack align="start" spacing={1} flex={1}>
              <Heading size="lg" color={textPrimary} fontWeight="700">
                Contact Directory
              </Heading>
              <Text color={textSecondary} fontSize="md">
                Manage contacts for {bot?.name || 'this bot'}. {filteredContacts.length} of {contacts.length} contacts
              </Text>
            </VStack>
            
            <ButtonGroup size="md" variant="outline">
              <Button
                leftIcon={<FiUpload />}
                borderColor={borderColor}
                color={textPrimary}
                _hover={{ borderColor: 'blue.400', color: 'blue.400' }}
              >
                Import
              </Button>
              <Button
                leftIcon={<FiDownload />}
                borderColor={borderColor}
                color={textPrimary}
                _hover={{ borderColor: 'green.400', color: 'green.400' }}
              >
                Export
              </Button>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                onClick={() => {
                  setEditingContact(null);
                  onOpen();
                }}
                _hover={{ transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                Add Contact
              </Button>
            </ButtonGroup>
          </Flex>
        </CardHeader>

        <CardBody pt={4}>
          {/* Search and Filter Controls */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={6}>
            <GridItem colSpan={{ base: 1, md: 2 }}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={textSecondary} />
                </InputLeftElement>
                <Input
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg={cardBg}
                  borderColor={borderColor}
                  color={textPrimary}
                  _focus={{ borderColor: 'blue.400' }}
                />
              </InputGroup>
            </GridItem>
            
            <GridItem>
              <Select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                bg={cardBg}
                borderColor={borderColor}
                color={textPrimary}
              >
                <option value="all">All Contacts</option>
                <option value="called">Called</option>
                <option value="not-called">Not Called</option>
                <option value="company">Has Company</option>
              </Select>
            </GridItem>

            <GridItem>
              <Select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                bg={cardBg}
                borderColor={borderColor}
                color={textPrimary}
              >
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="email-asc">Email A-Z</option>
                <option value="company-asc">Company A-Z</option>
              </Select>
            </GridItem>
          </Grid>

          {/* Bulk Actions Bar */}
          {selectedContacts.size > 0 && (
            <Alert status="info" borderRadius="xl" mb={4} bg="blue.50" borderColor="blue.200">
              <AlertIcon color="blue.500" />
              <Flex w="full" justify="space-between" align="center">
                <Text color="blue.700" fontWeight="600">
                  {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected
                </Text>
                <ButtonGroup size="sm">
                  <Button
                    leftIcon={<DeleteIcon />}
                    colorScheme="red"
                    variant="outline"
                    onClick={onDeleteModalOpen}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedContacts(new Set())}
                  >
                    Clear Selection
                  </Button>
                </ButtonGroup>
              </Flex>
            </Alert>
          )}

          {/* Contact List/Grid */}
          {filteredContacts.length === 0 ? (
            <VStack spacing={6} py={12}>
              <Box as={FiUsers} size="64px" color="gray.400" />
              <VStack spacing={2}>
                <Heading size="md" color={textSecondary}>
                  {contacts.length === 0 ? 'No contacts yet' : 'No contacts match your search'}
                </Heading>
                <Text color={textSecondary} textAlign="center">
                  {contacts.length === 0 
                    ? 'Add your first contact to get started'
                    : 'Try adjusting your search or filter criteria'
                  }
                </Text>
              </VStack>
              {contacts.length === 0 && (
                <Button
                  colorScheme="blue"
                  leftIcon={<AddIcon />}
                  onClick={() => {
                    setEditingContact(null);
                    onOpen();
                  }}
                >
                  Add First Contact
                </Button>
              )}
            </VStack>
          ) : (
            <>
              {/* Contact Table */}
              <TableContainer bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                <Table variant="simple">
                  <Thead bg={bg}>
                    <Tr>
                      <Th py={4}>
                        <Checkbox
                          isChecked={isAllSelected}
                          isIndeterminate={isIndeterminate}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </Th>
                      <Th py={4} color={textSecondary} fontSize="sm" fontWeight="600">Name</Th>
                      <Th py={4} color={textSecondary} fontSize="sm" fontWeight="600">Email</Th>
                      <Th py={4} color={textSecondary} fontSize="sm" fontWeight="600">Phone</Th>
                      <Th py={4} color={textSecondary} fontSize="sm" fontWeight="600">Company</Th>
                      <Th py={4} color={textSecondary} fontSize="sm" fontWeight="600">Status</Th>
                      <Th py={4} color={textSecondary} fontSize="sm" fontWeight="600">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedContacts.map((contact) => (
                      <Tr 
                        key={contact.id}
                        _hover={{ bg: bg }}
                        borderBottomWidth="1px"
                        borderColor={borderColor}
                      >
                        <Td py={4}>
                          <Checkbox
                            isChecked={selectedContacts.has(contact.id)}
                            onChange={(e) => handleSelectContact(contact.id, e.target.checked)}
                            colorScheme="blue"
                          />
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="600" color={textPrimary} fontSize="sm">
                              {contact.name}
                            </Text>
                            {contact.profile && (
                              <Text fontSize="xs" color={textTertiary} noOfLines={1}>
                                {contact.profile}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td py={4}>
                          {contact.email ? (
                            <Text 
                              fontSize="sm" 
                              color="blue.500" 
                              as="a" 
                              href={`mailto:${contact.email}`}
                              _hover={{ textDecoration: 'underline' }}
                            >
                              {contact.email}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color={textTertiary}>-</Text>
                          )}
                        </Td>
                        <Td py={4}>
                          {contact.phone ? (
                            <Text 
                              fontSize="sm" 
                              color="green.500"
                              as="a"
                              href={`tel:${contact.phone}`}
                              _hover={{ textDecoration: 'underline' }}
                            >
                              {contact.phone}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color={textTertiary}>-</Text>
                          )}
                        </Td>
                        <Td py={4}>
                          {contact.company ? (
                            <HStack spacing={1}>
                              <Box as={FiBriefcase} size="12px" color="blue.400" />
                              <Text fontSize="sm" color={textSecondary}>
                                {contact.company}
                              </Text>
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color={textTertiary}>-</Text>
                          )}
                        </Td>
                        <Td py={4}>
                          <Badge
                            colorScheme={contact.called ? 'green' : 'gray'}
                            borderRadius="full"
                            px={3}
                            py={1}
                            fontSize="xs"
                            fontWeight="600"
                          >
                            {contact.called ? 'Called' : 'Not Called'}
                          </Badge>
                        </Td>
                        <Td py={4}>
                          <ButtonGroup size="sm" isAttached variant="outline">
                            <Button
                              leftIcon={<FiEdit3 />}
                              colorScheme="blue"
                              onClick={() => {
                                setEditingContact(contact);
                                onOpen();
                              }}
                              size="sm"
                            >
                              Edit
                            </Button>
                            <Button
                              leftIcon={<FiTrash2 />}
                              colorScheme="red"
                              onClick={() => handleDeleteContact(contact.id)}
                              size="sm"
                            >
                              Delete
                            </Button>
                          </ButtonGroup>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filteredContacts.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </CardBody>
      </Card>

      {/* Contact Form Modal */}
      <ContactForm
        isOpen={isOpen}
        onClose={onClose}
        contact={editingContact}
        onSave={handleSaveContact}
        loading={loading}
        botId={bot?.id}
        
      />

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Contacts</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="xl">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">
                    Are you sure you want to delete {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''}?
                  </Text>
                  <Text fontSize="sm" mt={1}>
                    This action cannot be undone.
                  </Text>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleBulkDelete}
              isLoading={loading}
              loadingText="Deleting..."
            >
              Delete {selectedContacts.size} Contact{selectedContacts.size !== 1 ? 's' : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Pagination Controls Component
const PaginationControls = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.300');

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisible - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return (
    <Flex 
      justify="space-between" 
      align="center" 
      mt={6} 
      p={4} 
      borderTop="1px" 
      borderColor={borderColor}
      direction={{ base: 'column', md: 'row' }}
      gap={4}
    >
      <HStack spacing={4}>
        <Text fontSize="sm" color={textSecondary}>
          Showing {startItem} to {endItem} of {totalItems} contacts
        </Text>
        
        <HStack spacing={2}>
          <Text fontSize="sm" color={textSecondary}>
            Show:
          </Text>
          <Select
            size="sm"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            w="auto"
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </HStack>
      </HStack>

      <HStack spacing={2}>
        <IconButton
          icon={<ChevronLeftIcon />}
          size="sm"
          variant="outline"
          isDisabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        />
        
        {getPageNumbers().map(page => (
          <Button
            key={page}
            size="sm"
            variant={currentPage === page ? 'solid' : 'outline'}
            colorScheme={currentPage === page ? 'blue' : 'gray'}
            onClick={() => onPageChange(page)}
            minW={10}
          >
            {page}
          </Button>
        ))}
        
        <IconButton
          icon={<ChevronRightIcon />}
          size="sm"
          variant="outline"
          isDisabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        />
      </HStack>
    </Flex>
  );
};

export default RubricaManager;